import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useProjectAuth } from './useProjectAuth'
import * as XLSX from 'xlsx'
import { C, R, F, S, btnPrimary, btnSecondary, btnDanger, card, input, thStyle, kpiCard } from '../lib/designTokens'

const fmt  = n => new Intl.NumberFormat('en-US', { maximumFractionDigits:0 }).format(n||0)
const fmtD = s => s ? new Date(s).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'
const daysBetween = (a, b) => { if (!a||!b) return null; const d = (new Date(b)-new Date(a))/(1000*60*60*24); return Math.round(d) }
const addDays = (d, n) => { if (!d||n==null) return null; const dt=new Date(d); dt.setDate(dt.getDate()+n); return dt.toISOString().split('T')[0] }

const RESPONSIBLE_PARTIES = ['Client','Contractor','Designer','Force Majeure','Neutral','Shared']
const STATUSES = ['draft','submitted','under_review','approved','rejected','partially_approved','withdrawn']
const FIDIC_CLAUSES = [
  '1.9 — Delayed Drawings or Instructions',
  '2.1 — Right of Access to the Site',
  '4.7 — Setting Out',
  '4.12 — Unforeseeable Physical Conditions',
  '4.24 — Fossils',
  '7.4 — Testing',
  '8.4 — Extension of Time for Completion',
  '8.5 — Delays Caused by Authorities',
  '10.3 — Interference with Tests on Completion',
  '13.1 — Right to Vary',
  '13.7 — Adjustments for Changes in Legislation',
  "16.1 — Contractor's Entitlement to Suspend Work",
  "17.4 — Consequences of Employer's Risks",
  '19.4 — Consequences of Force Majeure',
  "20.1 — Contractor's Claims",
]

const STATUS_STYLE = {
  draft:              { bg:'#F3F4F6', color: C.sub,   border: C.border },
  submitted:          { bg:'#DBEAFE', color:'#1D4ED8', border:'#BFDBFE' },
  under_review:       { bg:'#FEF3C7', color: C.amber, border:'#FDE68A' },
  approved:           { bg:'#DCFCE7', color: C.green, border:'#BBF7D0' },
  rejected:           { bg:'#FEF2F2', color: C.red,   border:'#FECACA' },
  partially_approved: { bg:'#FED7AA', color:'#C2410C', border:'#FDBA74' },
  withdrawn:          { bg:'#F3F4F6', color: C.sub,   border: C.border },
}

const emptyForm = {
  eot_ref:'', title:'', cause:'', responsible_party:'Client',
  fidic_clause:'', notice_date:'', period_start:'', period_end:'',
  contract_completion_date:'', revised_completion_date:'',
  days_claimed:'', days_approved:'', status:'draft',
  prolongation_cost:'', prolongation_cost_approved:'',
  engineer_determination:'', linked_change_ref:'', notes:'',
}

export default function EOTRegister({ projectId: initProjectId }) {
  const [projects,     setProjects]    = useState([])
  const [projectId,    setProjectId]   = useState(null)
  const [projectName,  setProjectName] = useState('')
  const { can } = useProjectAuth(projectId)
  const [eots,         setEots]        = useState([])
  const [loading,      setLoading]     = useState(false)
  const [showModal,    setShowModal]   = useState(false)
  const [editEot,      setEditEot]     = useState(null)
  const [form,         setForm]        = useState(emptyForm)
  const [filterStatus, setFilterStatus]= useState('all')
  const [filterParty,  setFilterParty] = useState('all')
  const [saving,       setSaving]      = useState(false)
  const [expanded,     setExpanded]    = useState(null)
  const [selectedIds,  setSelectedIds] = useState(new Set())
  const [uploading,    setUploading]   = useState(false)
  const [uploadResult, setUploadResult]= useState(null)
  const fileInputRef = useRef(null)

  useEffect(() => {
    supabase.from('projects').select('id,name').order('name').then(({ data }) => setProjects(data||[]))
  }, [])
  useEffect(() => {
    if (initProjectId) {
      setProjectId(initProjectId)
      supabase.from('projects').select('name').eq('id',initProjectId).single().then(({data})=>setProjectName(data?.name||''))
    }
  }, [initProjectId])
  useEffect(() => { if (projectId) fetchEots() }, [projectId])

  async function fetchEots() {
    setLoading(true)
    const { data } = await supabase.from('eot_register').select('*').eq('project_id',projectId).order('created_at',{ascending:false})
    setEots(data||[])
    setLoading(false)
  }

  async function saveEot() {
    if (!form.title || !projectId) return
    setSaving(true)
    const dc = parseInt(form.days_claimed)||0
    const da = parseInt(form.days_approved)||0
    // auto-calc revised completion date
    const revDate = form.contract_completion_date && da > 0
      ? addDays(form.contract_completion_date, da) : form.revised_completion_date || null
    const payload = {
      project_id: projectId,
      eot_ref: form.eot_ref||null, title: form.title, cause: form.cause||null,
      responsible_party: form.responsible_party, fidic_clause: form.fidic_clause||null,
      notice_date: form.notice_date||null,
      period_start: form.period_start||null, period_end: form.period_end||null,
      contract_completion_date: form.contract_completion_date||null,
      revised_completion_date: revDate,
      days_claimed: dc, days_approved: da, days_rejected: dc - da,
      status: form.status,
      prolongation_cost: parseFloat(form.prolongation_cost)||0,
      prolongation_cost_approved: parseFloat(form.prolongation_cost_approved)||0,
      engineer_determination: form.engineer_determination||null,
      linked_change_ref: form.linked_change_ref||null,
      notes: form.notes||null,
      notice_compliant: form.notice_date && form.period_start
        ? daysBetween(form.period_start, form.notice_date) <= 28
        : null,
    }
    if (editEot) { await supabase.from('eot_register').update(payload).eq('id',editEot.id) }
    else         { await supabase.from('eot_register').insert(payload) }
    setSaving(false); setShowModal(false); setEditEot(null); setForm(emptyForm); fetchEots()
  }

  async function deleteEot(id) {
    if (!window.confirm('Delete this EOT?')) return
    await supabase.from('eot_register').delete().eq('id',id); fetchEots()
  }
  async function bulkDelete() {
    if (!selectedIds.size) return
    if (!window.confirm(`Delete ${selectedIds.size} EOT(s)?`)) return
    await supabase.from('eot_register').delete().in('id',[...selectedIds])
    setSelectedIds(new Set()); fetchEots()
  }
  function toggleSelect(id, e) { e.stopPropagation(); setSelectedIds(prev=>{ const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n }) }
  function toggleSelectAll()   { setSelectedIds(selectedIds.size===filtered.length?new Set():new Set(filtered.map(e=>e.id))) }
  function openEdit(e) {
    setEditEot(e)
    setForm({ ...emptyForm, ...e,
      days_claimed: e.days_claimed||'', days_approved: e.days_approved||'',
      prolongation_cost: e.prolongation_cost||'', prolongation_cost_approved: e.prolongation_cost_approved||'',
      period_start: e.period_start||'', period_end: e.period_end||'',
      notice_date: e.notice_date||'', contract_completion_date: e.contract_completion_date||'',
      revised_completion_date: e.revised_completion_date||'', linked_change_ref: e.linked_change_ref||'',
    })
    setShowModal(true)
  }

  function downloadTemplate() {
    const wb = XLSX.utils.book_new()
    const H = ['eot_ref','title','cause','responsible_party','fidic_clause','notice_date','period_start','period_end','contract_completion_date','days_claimed','days_approved','status','prolongation_cost','prolongation_cost_approved','engineer_determination','linked_change_ref','notes']
    const D = [
      ['EOT-001','Delay due to late issuance of structural drawings','Client failed to issue approved shop drawings within 21 days of submission. 14 drawings were delayed between 15–42 days, causing critical path delay to pile cap construction.','Client','1.9 — Delayed Drawings or Instructions','2024-02-28','2024-02-01','2024-04-15','2024-12-31',45,35,'approved',1850000,1600000,'Engineer determined 35 days extension under Cl.1.9. Prolongation cost assessed at SAR 1.6M (site overheads + labour).','VO-003','Delay notice issued 28 Feb 2024. Final determination issued 15 Jun 2024.'],
      ['EOT-002','Unforeseen ground conditions — additional piling works','Geotechnical investigation revealed soft layer at -18m not shown in site investigation report. Additional 140 piles required. Works caused 28-day delay to structure commencement.','Client','4.12 — Unforeseeable Physical Conditions','2024-03-20','2024-03-05','2024-04-02','2024-12-31',28,28,'approved',980000,980000,'Full entitlement granted under Cl.4.12. Unforeseeable conditions confirmed by independent geotechnical expert.','VO-001','Notice issued Day 15. Expert report provided. Full EOT and prolongation approved.'],
      ['EOT-003','Municipality permit delay — authority approval for temporary works','Temporary works permit application submitted on time. Authority delay of 21 days caused suspension of excavation works on critical path.','External','8.5 — Delays Caused by Authorities','2024-04-22','2024-04-01','2024-04-22','2024-12-31',21,18,'partially_approved',720000,580000,'Engineer granted 18 days — contractor partially responsible for incomplete permit documentation (3 days).','','Notice issued Day 21. Partially approved. Contractor to absorb 3 days cost.'],
      ['EOT-004','Variation Order VO-005 — additional facade scope','Client instruction to extend facade cladding on east elevation. Additional scope added 18 days to facade package programme.','Client','13.1 — Right to Vary','2024-05-10','2024-05-01','2024-05-19','2024-12-31',18,18,'approved',640000,640000,'Entitlement clear under Cl.13.1. Time and cost agreed by Engineer.','VO-005','Concurrent with other activities — no net delay to completion agreed.'],
      ['EOT-005','Force majeure — exceptional rainfall and flooding','Exceptional rainfall event (1-in-50-year return period) caused 12-day site closure. All access roads flooded. Force majeure notice issued within 14 days.','Force Majeure','19.4 — Consequences of Force Majeure','2024-06-17','2024-06-05','2024-06-17','2024-12-31',12,12,'approved',0,0,'Force majeure confirmed. No prolongation cost entitlement under Cl.19.4 — time extension only.','','Meteorological data confirmed. Both parties agreed no cost entitlement.'],
    ]
    const ws = XLSX.utils.aoa_to_sheet([H,...D])
    ws['!cols'] = H.map((_,i)=>({ wch: i===1?35:i===2||i===14||i===16?50:i===4?40:14 }))
    XLSX.utils.book_append_sheet(wb, ws, 'EOT_Register')
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['Field','Valid Values'],
      ['responsible_party','Client | Contractor | Designer | Force Majeure | Neutral | Shared'],
      ['status','draft | submitted | under_review | approved | rejected | partially_approved | withdrawn'],
      ['fidic_clause','1.9 | 2.1 | 4.7 | 4.12 | 4.24 | 7.4 | 8.4 | 8.5 | 10.3 | 13.1 | 13.7 | 16.1 | 17.4 | 19.4 | 20.1'],
      ['notice_date','FIDIC 20.1 requires notice within 28 days of event. System auto-checks compliance.'],
    ]), 'Valid_Values')
    XLSX.writeFile(wb, 'EOTRegister_Template.xlsx')
  }

  async function handleUpload(e) {
    const file = e.target.files[0]; if (!file||!projectId) return
    setUploading(true); setUploadResult(null)
    try {
      const buf = await file.arrayBuffer()
      const wb = XLSX.read(buf)
      const ws = wb.Sheets['EOT_Register']
      const rows = ws ? XLSX.utils.sheet_to_json(ws,{defval:''}) : []
      let imported=0, skipped=0; const errors=[]
      for (const row of rows) {
        const title = (row['title']||'').toString().trim()
        if (!title) { skipped++; continue }
        const dc = parseInt(row['days_claimed'])||0
        const da = parseInt(row['days_approved'])||0
        const ccd = row['contract_completion_date']?.toString().trim()||null
        const nd  = row['notice_date']?.toString().trim()||null
        const ps  = row['period_start']?.toString().trim()||null
        const revDate = ccd && da>0 ? addDays(ccd,da) : null
        const { error } = await supabase.from('eot_register').insert({
          project_id: projectId,
          eot_ref: row['eot_ref']?.toString().trim()||null, title,
          cause: row['cause']?.toString().trim()||null,
          responsible_party: row['responsible_party']?.toString().trim()||'Client',
          fidic_clause: row['fidic_clause']?.toString().trim()||null,
          notice_date: nd, period_start: ps, period_end: row['period_end']?.toString().trim()||null,
          contract_completion_date: ccd, revised_completion_date: revDate,
          days_claimed: dc, days_approved: da, days_rejected: dc-da,
          status: row['status']?.toString().trim()||'draft',
          prolongation_cost: parseFloat(row['prolongation_cost'])||0,
          prolongation_cost_approved: parseFloat(row['prolongation_cost_approved'])||0,
          engineer_determination: row['engineer_determination']?.toString().trim()||null,
          linked_change_ref: row['linked_change_ref']?.toString().trim()||null,
          notes: row['notes']?.toString().trim()||null,
          notice_compliant: nd && ps ? daysBetween(ps,nd) <= 28 : null,
        })
        if (error) { errors.push(`${row['eot_ref']||title.slice(0,25)}: ${error.message}`); skipped++ } else imported++
      }
      setUploadResult({imported,skipped,errors}); await fetchEots()
    } catch(err) { setUploadResult({imported:0,skipped:0,errors:[err.message]}) }
    setUploading(false); if (fileInputRef.current) fileInputRef.current.value=''
  }

  const filtered = eots.filter(e => {
    if (filterStatus!=='all' && e.status!==filterStatus) return false
    if (filterParty !=='all' && e.responsible_party!==filterParty) return false
    return true
  })
  const totalClaimed   = eots.reduce((s,e)=>s+(e.days_claimed||0),0)
  const totalApproved  = eots.reduce((s,e)=>s+(e.days_approved||0),0)
  const totalRejected  = eots.reduce((s,e)=>s+(e.days_rejected||0),0)
  const totalCost      = eots.reduce((s,e)=>s+(e.prolongation_cost_approved||e.prolongation_cost||0),0)
  const pending        = eots.filter(e=>['submitted','under_review'].includes(e.status)).length
  const lastCCD        = eots.filter(e=>e.revised_completion_date).sort((a,b)=>new Date(b.revised_completion_date)-new Date(a.revised_completion_date))[0]
  const noticeWarnings = eots.filter(e=>e.notice_compliant===false).length

  const badge = st => { const s=STATUS_STYLE[st]||STATUS_STYLE.draft; return { display:'inline-flex', alignItems:'center', padding:'2px 10px', borderRadius: R.badge, fontSize: F.caption, fontWeight:600, background:s.bg, color:s.color, border:`1px solid ${s.border}` } }
  const pg = { padding: S.pagePad, minHeight:'100vh', background: C.pageBg, color: C.ink, fontFamily:'Inter,sans-serif', fontSize: F.body }

  return (
    <div style={pg}>
      {/* ── Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize: F.heading, fontWeight:700, color: C.ink, margin:0 }}>EOT Register</h1>
          <p style={{ fontSize: F.caption, color: C.sub, marginTop:3 }}>Extension of Time Claims — FIDIC Red Book (Cl. 8.4 / 20.1)</p>
          {projectName && <p style={{ fontSize: F.body, color: C.sub, marginTop:2 }}>{projectName}</p>}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {!initProjectId && (
            <select value={projectId||''} onChange={e=>{ setProjectId(e.target.value); supabase.from('projects').select('name').eq('id',e.target.value).single().then(({data})=>setProjectName(data?.name||'')) }} style={{ ...input, width:'auto' }}>
              <option value="">Select project…</option>
              {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <button onClick={downloadTemplate} style={{ ...btnSecondary, fontSize: F.body }}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2"/><polyline points="5,7 8,10 11,7"/><line x1="8" y1="2" x2="8" y2="10"/></svg>
            Template
          </button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display:'none' }} onChange={handleUpload}/>
          {can('EOT.Create') && (
            <button onClick={()=>fileInputRef.current?.click()} disabled={uploading||!projectId} style={{ ...btnSecondary, fontSize: F.body, borderColor: C.blue, color: C.blue, opacity:uploading||!projectId?0.5:1 }}>
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2"/><polyline points="5,6 8,3 11,6"/><line x1="8" y1="3" x2="8" y2="11"/></svg>
              {uploading?'Uploading…':'Upload Excel'}
            </button>
          )}
          {can('EOT.Create') && (
            <button onClick={()=>{ setEditEot(null); setForm(emptyForm); setShowModal(true) }} style={{ ...btnPrimary, fontSize: F.body }}>+ New EOT</button>
          )}
        </div>
      </div>

      {/* ── Upload result */}
      {uploadResult && (
        <div style={{ marginBottom:16, padding:'12px 16px', borderRadius: R.card, background:uploadResult.errors.length?'#FEF3C7':'#F0FDF4', border:`1px solid ${uploadResult.errors.length?'#FDE68A':'#BBF7D0'}` }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
            <span style={{ fontWeight:600, fontSize: F.body, color:uploadResult.errors.length?C.amber:C.green }}>{uploadResult.errors.length===0?'✓ Upload successful':'⚠ Upload with issues'}</span>
            <button onClick={()=>setUploadResult(null)} style={{ background:'none', border:'none', cursor:'pointer', color: C.sub, fontSize:14 }}>✕</button>
          </div>
          <p style={{ fontSize: F.caption, color: C.ink2, margin:0 }}>Imported: <b>{uploadResult.imported}</b> · Skipped: <b>{uploadResult.skipped}</b></p>
          {uploadResult.errors.map((e,i)=><p key={i} style={{ fontSize: F.caption, color: C.amber, margin:'2px 0 0' }}>• {e}</p>)}
        </div>
      )}

      {/* ── FIDIC 20.1 Notice Warning */}
      {noticeWarnings > 0 && (
        <div style={{ marginBottom:16, padding:'10px 16px', borderRadius: R.card, background:'#FEF3C7', border:`1px solid #FDE68A`, display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:16 }}>⚠️</span>
          <span style={{ fontSize: F.body, color: C.amber, fontWeight:600 }}>{noticeWarnings} EOT claim{noticeWarnings>1?'s':''} with late notice (FIDIC Cl.20.1 — 28-day notice required). Risk of Engineer rejecting entitlement.</span>
        </div>
      )}

      {/* ── KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:12, marginBottom:20 }}>
        {[
          { label:'Days Claimed',           val:`${totalClaimed}d`,          color: C.amber },
          { label:'Days Approved',          val:`${totalApproved}d`,         color: C.green },
          { label:'Days Rejected',          val:`${totalRejected}d`,         color: C.red   },
          { label:'Pending Review',         val:`${pending} EOTs`,           color: C.blue  },
          { label:'Prolongation Cost',      val:`SAR ${fmt(totalCost)}`,     color: C.ink, mono:true },
          { label:'Revised Completion',     val: lastCCD ? fmtD(lastCCD.revised_completion_date) : 'No EOTs', color: C.ink },
        ].map(k=>(
          <div key={k.label} style={{ ...kpiCard }}>
            <p style={{ fontSize: F.label, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color: C.sub, margin:'0 0 6px' }}>{k.label}</p>
            <p style={{ fontSize: k.label==='Revised Completion'?F.bodyLg:F.kpi, fontWeight:700, color: k.color, margin:0, fontFamily:k.mono?'JetBrains Mono,monospace':'inherit', lineHeight:1.1 }}>{k.val}</p>
          </div>
        ))}
      </div>

      {/* ── Bulk bar */}
      {selectedIds.size>0 && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12, padding:'10px 16px', borderRadius: R.card, background:'#FEF2F2', border:`1px solid ${C.redLine}` }}>
          <span style={{ fontWeight:600, fontSize: F.body, color: C.red }}>{selectedIds.size} EOT{selectedIds.size>1?'s':''} selected</span>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={()=>setSelectedIds(new Set())} style={{ ...btnSecondary, padding:'5px 12px', fontSize: F.caption }}>Cancel</button>
            {can('EOT.Delete') && (
              <button onClick={bulkDelete} style={{ ...btnDanger, padding:'5px 12px', fontSize: F.caption }}>
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><polyline points="3,4 13,4"/><path d="M5 4V2h6v2"/><path d="M4 4l1 10h6l1-10"/></svg>
                Delete {selectedIds.size} selected
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Filters */}
      <div style={{ display:'flex', gap:8, marginBottom:12 }}>
        <select value={filterParty} onChange={e=>setFilterParty(e.target.value)} style={{ ...input, width:'auto', fontSize: F.caption }}>
          <option value="all">All Parties</option>
          {RESPONSIBLE_PARTIES.map(p=><option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{ ...input, width:'auto', fontSize: F.caption }}>
          <option value="all">All Statuses</option>
          {STATUSES.map(s=><option key={s} value={s}>{s.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>)}
        </select>
        {/* Select all checkbox */}
        <label style={{ display:'flex', alignItems:'center', gap:6, marginLeft:'auto', fontSize: F.caption, color: C.sub, cursor:'pointer' }}>
          <input type="checkbox" checked={filtered.length>0&&selectedIds.size===filtered.length} onChange={toggleSelectAll} style={{ cursor:'pointer', accentColor: C.blue }}/>
          Select all
        </label>
      </div>

      {/* ── EOT List */}
      <div style={{ ...card, padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:48, textAlign:'center', color: C.sub }}>Loading…</div>
        : filtered.length===0 ? <div style={{ padding:48, textAlign:'center', color: C.sub }}>No EOT records yet — click "+ New EOT" or upload template</div>
        : filtered.map((e, idx) => (
          <div key={e.id} style={{ borderTop: idx>0 ? `1px solid ${C.border}` : 'none' }}>
            {/* Row */}
            <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', cursor:'pointer', background: expanded===e.id ? C.surfaceAlt : C.surface }}
              onClick={()=>setExpanded(expanded===e.id?null:e.id)}>
              {/* Checkbox */}
              <div onClick={ev=>toggleSelect(e.id,ev)} style={{ flexShrink:0 }}>
                <input type="checkbox" checked={selectedIds.has(e.id)} onChange={ev=>toggleSelect(e.id,ev)} style={{ cursor:'pointer', accentColor: C.blue }}/>
              </div>
              {/* Ref */}
              <div style={{ width:72, fontFamily:'monospace', fontSize: F.caption, color: C.sub, flexShrink:0 }}>{e.eot_ref||'—'}</div>
              {/* Title + meta */}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                  <span style={{ fontWeight:600, fontSize: F.body, color: C.ink }}>{e.title}</span>
                  {e.notice_compliant===false && <span style={{ fontSize: F.caption, color: C.red, background:'#FEF2F2', border:`1px solid #FECACA`, borderRadius: R.tag, padding:'1px 6px' }}>⚠ Late Notice</span>}
                  {e.linked_change_ref && <span style={{ fontSize: F.caption, color: C.blue }}>🔗 {e.linked_change_ref}</span>}
                </div>
                <div style={{ fontSize: F.caption, color: C.sub, marginTop:3, display:'flex', gap:12, flexWrap:'wrap' }}>
                  {e.fidic_clause && <span>📋 Cl. {e.fidic_clause.split('—')[0].trim()}</span>}
                  <span>👤 {e.responsible_party}</span>
                  {e.period_start && <span>📅 {fmtD(e.period_start)} → {e.period_end ? fmtD(e.period_end) : '…'}</span>}
                  {e.notice_date && <span>📬 Notice: {fmtD(e.notice_date)}</span>}
                </div>
              </div>
              {/* Days */}
              <div style={{ display:'flex', gap:16, flexShrink:0 }}>
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontSize: F.caption, color: C.sub }}>Claimed</div>
                  <div style={{ fontFamily:'monospace', fontWeight:700, fontSize: F.bodyLg, color: C.amber }}>{e.days_claimed||0}d</div>
                </div>
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontSize: F.caption, color: C.sub }}>Approved</div>
                  <div style={{ fontFamily:'monospace', fontWeight:700, fontSize: F.bodyLg, color: C.green }}>{e.days_approved||0}d</div>
                </div>
                {(e.days_rejected||0) > 0 && (
                  <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize: F.caption, color: C.sub }}>Rejected</div>
                    <div style={{ fontFamily:'monospace', fontWeight:700, fontSize: F.bodyLg, color: C.red }}>{e.days_rejected}d</div>
                  </div>
                )}
                {(e.prolongation_cost_approved||e.prolongation_cost||0) > 0 && (
                  <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize: F.caption, color: C.sub }}>Prol. Cost</div>
                    <div style={{ fontFamily:'monospace', fontWeight:600, fontSize: F.caption, color: C.ink }}>SAR {fmt(e.prolongation_cost_approved||e.prolongation_cost)}</div>
                  </div>
                )}
              </div>
              <span style={badge(e.status)}>{e.status?.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}</span>
              {/* Actions */}
              <div style={{ display:'flex', gap:4, flexShrink:0 }} onClick={ev=>ev.stopPropagation()}>
                {can('EOT.Edit') && (
                  <button onClick={()=>openEdit(e)} style={{ background:'none', border:'none', cursor:'pointer', color: C.sub, padding:4, borderRadius: R.tag }}>
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M11 2l3 3-9 9H2v-3L11 2z"/></svg>
                  </button>
                )}
                {can('EOT.Delete') && (
                  <button onClick={()=>deleteEot(e.id)} style={{ background:'none', border:'none', cursor:'pointer', color: C.red, padding:4, borderRadius: R.tag }}>
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><polyline points="3,4 13,4"/><path d="M5 4V2h6v2"/><path d="M4 4l1 10h6l1-10"/></svg>
                  </button>
                )}
              </div>
            </div>
            {/* Expanded detail */}
            {expanded===e.id && (
              <div style={{ padding:'14px 20px 16px', borderTop:`1px solid ${C.border}`, background: C.surfaceAlt }}>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:12 }}>
                  <div>
                    <p style={{ fontSize: F.caption, fontWeight:600, color: C.sub, margin:'0 0 4px' }}>Contract Completion Date</p>
                    <p style={{ fontSize: F.body, fontWeight:600, color: C.ink, margin:0 }}>{fmtD(e.contract_completion_date)}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: F.caption, fontWeight:600, color: C.sub, margin:'0 0 4px' }}>Revised Completion Date</p>
                    <p style={{ fontSize: F.body, fontWeight:600, color: C.blue, margin:0 }}>{fmtD(e.revised_completion_date)}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: F.caption, fontWeight:600, color: C.sub, margin:'0 0 4px' }}>Prolongation Claimed</p>
                    <p style={{ fontSize: F.body, fontWeight:600, color: C.ink, margin:0 }}>SAR {fmt(e.prolongation_cost)}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: F.caption, fontWeight:600, color: C.sub, margin:'0 0 4px' }}>Prolongation Approved</p>
                    <p style={{ fontSize: F.body, fontWeight:600, color: C.green, margin:0 }}>SAR {fmt(e.prolongation_cost_approved)}</p>
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                  {e.cause && <div><p style={{ fontSize: F.caption, fontWeight:600, color: C.sub, margin:'0 0 4px' }}>Cause / Narrative</p><p style={{ fontSize: F.body, color: C.ink2, margin:0, lineHeight:1.5 }}>{e.cause}</p></div>}
                  {e.engineer_determination && <div><p style={{ fontSize: F.caption, fontWeight:600, color: C.sub, margin:'0 0 4px' }}>Engineer's Determination</p><p style={{ fontSize: F.body, color: C.ink2, margin:0, lineHeight:1.5 }}>{e.engineer_determination}</p></div>}
                  {e.notes && <div><p style={{ fontSize: F.caption, fontWeight:600, color: C.sub, margin:'0 0 4px' }}>Notes</p><p style={{ fontSize: F.body, color: C.ink2, margin:0, lineHeight:1.5 }}>{e.notes}</p></div>}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Modal */}
      {showModal && (
        <div style={{ position:'fixed', inset:0, zIndex:50, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(15,16,23,.45)' }}>
          <div style={{ background: C.surface, borderRadius: R.card, width:'100%', maxWidth:720, maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,.15)', margin:'0 16px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 20px', borderBottom:`1px solid ${C.border}` }}>
              <div>
                <h2 style={{ margin:0, fontSize: F.title, fontWeight:700, color: C.ink }}>{editEot?'Edit EOT':'New EOT Claim'}</h2>
                <p style={{ margin:'2px 0 0', fontSize: F.caption, color: C.sub }}>FIDIC Extension of Time — Cl. 8.4 / 20.1</p>
              </div>
              <button onClick={()=>{ setShowModal(false); setEditEot(null); setForm(emptyForm) }} style={{ background:'none', border:'none', cursor:'pointer', color: C.sub, padding:4 }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="3" y1="3" x2="13" y2="13"/><line x1="13" y1="3" x2="3" y2="13"/></svg>
              </button>
            </div>
            <div style={{ padding:'16px 20px', overflowY:'auto', display:'flex', flexDirection:'column', gap:14 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                <div><label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>EOT Ref</label><input value={form.eot_ref} onChange={e=>setForm(f=>({...f,eot_ref:e.target.value}))} placeholder="EOT-001" style={input}/></div>
                <div><label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>Status</label><select value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))} style={{ ...input, background: C.surface }}>{STATUSES.map(s=><option key={s} value={s}>{s.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>)}</select></div>
                <div><label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>Responsible Party</label><select value={form.responsible_party} onChange={e=>setForm(f=>({...f,responsible_party:e.target.value}))} style={{ ...input, background: C.surface }}>{RESPONSIBLE_PARTIES.map(p=><option key={p} value={p}>{p}</option>)}</select></div>
              </div>
              <div><label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>Title / Event Description *</label><input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="e.g. Delay due to late drawings" style={input}/></div>
              <div><label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>FIDIC Clause</label><select value={form.fidic_clause} onChange={e=>setForm(f=>({...f,fidic_clause:e.target.value}))} style={{ ...input, background: C.surface }}><option value="">Select clause…</option>{FIDIC_CLAUSES.map(c=><option key={c} value={c}>{c}</option>)}</select></div>
              {/* FIDIC 20.1 compliance */}
              <div style={{ padding:'12px 14px', borderRadius: R.input, background: C.surfaceAlt, border:`1px solid ${C.border}` }}>
                <p style={{ margin:'0 0 8px', fontSize: F.caption, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color: C.sub }}>FIDIC 20.1 — Notice & Time Period</p>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                  <div><label style={{ display:'block', fontSize: F.caption, color: C.sub, marginBottom:4 }}>Delay Event Start</label><input type="date" value={form.period_start} onChange={e=>setForm(f=>({...f,period_start:e.target.value}))} style={input}/></div>
                  <div><label style={{ display:'block', fontSize: F.caption, color: C.sub, marginBottom:4 }}>Delay Event End</label><input type="date" value={form.period_end} onChange={e=>setForm(f=>({...f,period_end:e.target.value}))} style={input}/></div>
                  <div>
                    <label style={{ display:'block', fontSize: F.caption, color: C.sub, marginBottom:4 }}>Notice Date (Cl.20.1 — 28 days)</label>
                    <input type="date" value={form.notice_date} onChange={e=>setForm(f=>({...f,notice_date:e.target.value}))} style={{ ...input, borderColor: form.notice_date && form.period_start && daysBetween(form.period_start,form.notice_date)>28 ? C.red : C.border2 }}/>
                    {form.notice_date && form.period_start && (
                      <p style={{ fontSize: F.caption, margin:'4px 0 0', color: daysBetween(form.period_start,form.notice_date)>28?C.red:C.green }}>
                        {daysBetween(form.period_start,form.notice_date)} days from event {daysBetween(form.period_start,form.notice_date)>28?'⚠ Late notice — risk of rejection':'✓ Compliant'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              {/* Days */}
              <div style={{ padding:'12px 14px', borderRadius: R.input, background:'#F0FDF4', border:`1px solid ${C.greenLine}` }}>
                <p style={{ margin:'0 0 8px', fontSize: F.caption, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color: C.green }}>Time Impact Assessment</p>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:12 }}>
                  <div><label style={{ display:'block', fontSize: F.caption, color: C.sub, marginBottom:4 }}>Contract Completion Date</label><input type="date" value={form.contract_completion_date} onChange={e=>setForm(f=>({...f,contract_completion_date:e.target.value}))} style={input}/></div>
                  <div><label style={{ display:'block', fontSize: F.caption, color: C.sub, marginBottom:4 }}>Days Claimed</label><input type="number" value={form.days_claimed} onChange={e=>setForm(f=>({...f,days_claimed:e.target.value}))} placeholder="0" style={input}/></div>
                  <div><label style={{ display:'block', fontSize: F.caption, color: C.sub, marginBottom:4 }}>Days Approved</label><input type="number" value={form.days_approved} onChange={e=>setForm(f=>({...f,days_approved:e.target.value}))} placeholder="0" style={input}/></div>
                  <div>
                    <label style={{ display:'block', fontSize: F.caption, color: C.sub, marginBottom:4 }}>Revised Completion (auto)</label>
                    <div style={{ ...input, background: C.surfaceAlt, color: C.blue, fontWeight:600 }}>
                      {form.contract_completion_date && form.days_approved
                        ? fmtD(addDays(form.contract_completion_date, parseInt(form.days_approved)||0))
                        : '—'}
                    </div>
                  </div>
                </div>
              </div>
              {/* Cost */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                <div><label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>Prolongation Cost Claimed (SAR)</label><input type="number" value={form.prolongation_cost} onChange={e=>setForm(f=>({...f,prolongation_cost:e.target.value}))} placeholder="0" style={input}/></div>
                <div><label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>Prolongation Cost Approved (SAR)</label><input type="number" value={form.prolongation_cost_approved} onChange={e=>setForm(f=>({...f,prolongation_cost_approved:e.target.value}))} placeholder="0" style={input}/></div>
                <div><label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>Linked VO / Change Ref</label><input value={form.linked_change_ref} onChange={e=>setForm(f=>({...f,linked_change_ref:e.target.value}))} placeholder="VO-001" style={input}/></div>
              </div>
              <div><label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>Cause / Narrative</label><textarea value={form.cause} onChange={e=>setForm(f=>({...f,cause:e.target.value}))} rows={2} style={{ ...input, resize:'none' }}/></div>
              <div><label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>Engineer's Determination</label><textarea value={form.engineer_determination} onChange={e=>setForm(f=>({...f,engineer_determination:e.target.value}))} rows={2} style={{ ...input, resize:'none' }}/></div>
              <div><label style={{ display:'block', fontSize: F.caption, fontWeight:600, color: C.sub, marginBottom:4 }}>Notes</label><textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} rows={2} style={{ ...input, resize:'none' }}/></div>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:8, padding:'14px 20px', borderTop:`1px solid ${C.border}` }}>
              <button onClick={()=>{ setShowModal(false); setEditEot(null); setForm(emptyForm) }} style={btnSecondary}>Cancel</button>
              <button onClick={saveEot} disabled={saving||!form.title} style={{ ...btnPrimary, opacity:saving||!form.title?0.6:1 }}>
                {saving?'Saving…':(editEot?'Update EOT':'Submit EOT')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
